import { useEffect } from "react";
import { quarterRange, yearRange, type Quarter } from "../lib/filters.js";
import type { DocumentType } from "../hooks/useInvoices.js";

/**
 * Typy, které evidujeme — a tedy jediné, co se může v tabulce objevit.
 * Dodací listy, objednávky a nedokumenty backend ukládá jako `not_invoice`
 * a query je filtruje už přes `status`.
 */
export const FILTERABLE_DOCUMENT_TYPES: DocumentType[] = [
  "invoice",
  "proforma",
  "payment_request",
  "set_off",
  "credit_note",
  "receivables_confirmation",
];

/** Default zaškrtnutí — všechny evidované typy. */
export const DEFAULT_DOCUMENT_TYPES: DocumentType[] = [
  ...FILTERABLE_DOCUMENT_TYPES,
];

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  invoice: "Faktura",
  proforma: "Proforma",
  payment_request: "Výzva k platbě",
  set_off: "Zápočet",
  credit_note: "Dobropis",
  delivery_note: "Dodací / záruční list",
  receivables_confirmation: "Odsouhlasení pohledávek",
  order: "Objednávka",
  other: "Jiné",
};

export interface GlobalFiltersValue {
  from: string;
  to: string;
  year: number;
  quarter: Quarter | "all";
  supplier: string;
  documentTypes: DocumentType[];
}

interface Props {
  value: GlobalFiltersValue;
  onChange: (next: GlobalFiltersValue) => void;
}

export function GlobalFilters({ value, onChange }: Props) {
  // Auto-sync data range with year/quarter selection
  useEffect(() => {
    const range =
      value.quarter === "all"
        ? yearRange(value.year)
        : quarterRange(value.year, value.quarter);
    if (range.from !== value.from || range.to !== value.to) {
      onChange({ ...value, ...range });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.year, value.quarter]);

  return (
    <div className="flex flex-wrap gap-4 items-end p-4 bg-white border border-slate-200 rounded-lg">
      <Field label="Rok">
        <select
          className="border border-slate-300 rounded px-2 py-1"
          value={value.year}
          onChange={(e) => onChange({ ...value, year: Number(e.target.value) })}
        >
          {yearOptions().map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Kvartál">
        <select
          className="border border-slate-300 rounded px-2 py-1"
          value={value.quarter}
          onChange={(e) =>
            onChange({
              ...value,
              quarter:
                e.target.value === "all"
                  ? "all"
                  : (Number(e.target.value) as Quarter),
            })
          }
        >
          <option value="all">Celý rok</option>
          <option value={1}>Q1</option>
          <option value={2}>Q2</option>
          <option value={3}>Q3</option>
          <option value={4}>Q4</option>
        </select>
      </Field>
      <Field label="Datum od">
        <input
          type="date"
          className="border border-slate-300 rounded px-2 py-1"
          value={value.from}
          onChange={(e) =>
            onChange({ ...value, from: e.target.value, quarter: "all" })
          }
        />
      </Field>
      <Field label="Datum do">
        <input
          type="date"
          className="border border-slate-300 rounded px-2 py-1"
          value={value.to}
          onChange={(e) =>
            onChange({ ...value, to: e.target.value, quarter: "all" })
          }
        />
      </Field>
      <Field label="Dodavatel obsahuje">
        <input
          type="text"
          className="border border-slate-300 rounded px-2 py-1"
          value={value.supplier}
          placeholder="napiš část jména…"
          onChange={(e) => onChange({ ...value, supplier: e.target.value })}
        />
      </Field>
      <Field label="Typ dokumentu">
        <div className="flex flex-wrap gap-3 pt-1">
          {FILTERABLE_DOCUMENT_TYPES.map((t) => {
            const checked = value.documentTypes.includes(t);
            return (
              <label
                key={t}
                className="flex items-center gap-1 text-sm text-slate-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...value.documentTypes, t]
                      : value.documentTypes.filter((x) => x !== t);
                    onChange({ ...value, documentTypes: next });
                  }}
                />
                {DOCUMENT_TYPE_LABELS[t]}
              </label>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col text-xs text-slate-500 gap-1">
      <span>{label}</span>
      {children}
    </label>
  );
}

function yearOptions(): number[] {
  const current = new Date().getUTCFullYear();
  const out: number[] = [];
  for (let y = current; y >= current - 6; y -= 1) out.push(y);
  return out;
}
